import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { API_URL } from "../config";
import { Paperclip } from "lucide-react";
import { getAdminSocket, connectAdminSocket } from "../socket";
import { ImageDown } from "lucide-react";
import { Trash } from "lucide-react";
import { Pencil } from "lucide-react";

const SHIPPING_OPTIONS = [
  { value: "in person", label: "In Person" },
  { value: "royal mail", label: "Royal Mail" },
];

const JOB_TYPES = [
  {
    value: "Monolithic Zirconia",
    label: "Monolithic Zirconia (5 working days)",
    days: 5,
  },
  {
    value: "Layered zirconia",
    label: "Layered zirconia (7 working days)",
    days: 7,
  },
  {
    value: "Porcelain bonded crown",
    label: "Porcelain bonded crown (7 working days)",
    days: 7,
  },
  { value: "Venirs", label: "Venirs (10 working days)", days: 10 },
  { value: "Emax", label: "Emax (10 working days)", days: 10 },
  { value: "Composite", label: "Composite (7 working days)", days: 7 },
  {
    value: "Temporary crown Pmma",
    label: "Temporary crown Pmma (7 working days)",
    days: 7,
  },
  {
    value: "Screw retained crown",
    label: "Screw retained crown (7 working days)",
    days: 7,
  },
  { value: "Splints", label: "Splints (5 working days)", days: 5 },
  { value: "Order", label: "Order (to discuss)", days: 0 },
];

// Funcție pentru a calcula data minimă (doar viitor)
const getMinDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

interface Order {
  id: number;
  orderNo: string;
  status: string;
  orderDate: string;
  shippedDate?: string | null;
  trackingNo?: string | null;
  message?: string | null;
  cost?: number | null;
  description?: string | null;
  shippedBy?: string | null;
  jobType?: string | null;
  orderRef?: string | null;
  estimatedDate?: string | null;
  imageName?: string | null;
  imageUrl?: string | null;
}

interface ChatMessage {
  id: number;
  orderId: number;
  sender: string;
  messageType: string;
  content: string;
  fileName?: string | null;
  fileUrl?: string | null;
  timestamp: string;
}

interface FileItem {
  fileName: string;
  fileUrl: string;
  uploadedBy: string;
  timestamp: string;
  type: "file" | "image";
}

const EditOrderAdmin = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  // State pentru editare
  const [status, setStatus] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [shippedDate, setShippedDate] = useState("");
  const [estimatedDate, setEstimatedDate] = useState(""); // NOU
  const [shippedBy, setShippedBy] = useState(""); // NOU
  const [jobType, setJobType] = useState(""); // NOU

  // State pentru chat
  const [messageInput, setMessageInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileItem[]>([]);

  const token = localStorage.getItem("token");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Funcție pentru a marca comanda ca văzută
  const markOrderAsSeen = async (orderId: number) => {
    try {
      await axios.put(
        `${API_URL}/admin/orders/${orderId}/mark-seen`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error: unknown) {
      console.error("Error marking order as seen:", error);
    }
  };

  // Funcție pentru ștergerea unui mesaj
  const handleDeleteMessage = async (messageId: number) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Actualizează local state-ul
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));

      const socket = getAdminSocket();
      if (!socket) return;
      // Emite eveniment prin socket pentru a notifica alte clienti
      socket.emit("messageDeleted", {
        orderId: Number(orderId),
        messageId: messageId,
      });
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Error deleting message");
    }
  };

  // Funcție pentru descărcarea unui fișier
  const handleDownload = (fileName: string) => {
    const downloadUrl = `${API_URL}/download/${fileName}`;
    window.open(downloadUrl, "_blank");
  };

  // State pentru editarea mesajului
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editMessageContent, setEditMessageContent] = useState("");

  // Funcție pentru a începe editarea
  const startEditMessage = (messageId: number, currentContent: string) => {
    setEditingMessageId(messageId);
    setEditMessageContent(currentContent);
  };

  // Funcție pentru a anula editarea
  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditMessageContent("");
  };

  // Funcție pentru a salva mesajul editat
  const handleEditMessage = async (messageId: number) => {
    if (!editMessageContent.trim()) {
      alert("Message cannot be empty");
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/messages/${messageId}`,
        {
          content: editMessageContent.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Actualizează local state-ul
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, content: editMessageContent.trim() }
            : msg,
        ),
      );

      const socket = getAdminSocket();
      if (!socket) return;

      // Emite eveniment prin socket pentru a notifica alte clienti
      socket.emit("messageEdited", {
        orderId: Number(orderId),
        messageId: messageId,
        newContent: editMessageContent.trim(),
      });

      // Resetează starea de editare
      setEditingMessageId(null);
      setEditMessageContent("");
    } catch (error) {
      console.error("Error editing message:", error);
      alert("Error editing message");
    }
  };

  // Fetch order data
  useEffect(() => {
    if (!token) {
      navigate("/admin-login");
      return;
    }

    const fetchOrder = async () => {
      try {
        const id = Number(orderId);
        const res = await axios.get(`${API_URL}/admin/order/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = res.data as Order;
        const apiData = res.data as any;
        setStatus(data.status || "");
        setTrackingNo(data.trackingNo || "");
        setShippedBy(data.shippedBy || "");
        setJobType(data.jobType || "");

        // Pentru estimated date (fără oră)
        if (data.estimatedDate) {
          const date = new Date(data.estimatedDate);
          setEstimatedDate(date.toISOString().split("T")[0]);
        } else {
          setEstimatedDate("");
        }

        setOrder(data);

        // Set initial chat messages - read messages from API response if present
        if (apiData.messages && Array.isArray(apiData.messages)) {
          setMessages(
            apiData.messages.sort(
              (a: any, b: any) =>
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime(),
            ),
          );
        }

        // Marchează comanda ca văzută automat
        if (id) {
          markOrderAsSeen(id);
        }

        if (data.shippedDate) {
          const date = new Date(data.shippedDate);
          setShippedDate(date.toISOString().slice(0, 16)); // Format pentru datetime-local
        } else {
          setShippedDate("");
        }
      } catch (error: unknown) {
        console.error("Error fetching order:", error);
        // Verificare simplă pentru axios error
        if (error && typeof error === "object" && "response" in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404) {
            alert("Order not found");
            navigate("/admin");
            return;
          }
        }
        alert("Failed to fetch order data");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate, token]);

  useEffect(() => {
    connectAdminSocket();
  }, []);

  // Socket.IO connection for chat - ensure listeners registered BEFORE joining room
  useEffect(() => {
    if (!orderId) return;

    const socket = getAdminSocket();
    if (!socket) {
      console.warn("❌ Admin socket not initialized");
      return;
    }

    const roomName = `order_${orderId}`;

    console.log(`
╔════════════════════════════════════════════════════════════╗
║ 🔌 SETTING UP ADMIN SOCKET LISTENERS (EditOrderAdmin)      ║
║ Socket ID: ${socket?.id || "not-connected-yet"}
║ Room: ${roomName}
║ Socket Connected: ${socket?.connected}
║ Timestamp: ${new Date().toISOString()}
╚════════════════════════════════════════════════════════════╝`);

    const handleConnect = () => {
      console.log(`
╔════════════════════════════════════════════════════════════╗
║ ✅ ADMIN SOCKET CONNECTED (EditOrderAdmin)                 ║
║ Socket ID: ${socket?.id}
║ Room: ${roomName}
║ Namespace: /admin
║ Timestamp: ${new Date().toISOString()}
╚════════════════════════════════════════════════════════════╝`);
      setIsConnected(true);

      socket.emit("joinOrder", Number(orderId));
      console.log(`📤 Emitted joinOrder for orderId: ${orderId}`);
    };

    const handleDisconnect = () => {
      console.log("Socket.IO disconnected in EditOrderAdmin");
      setIsConnected(false);
    };

    const handleMessageHistory = (data: any) => {
      console.log(`
╔════════════════════════════════════════════════════════════╗
║ 📥 MESSAGE HISTORY RECEIVED (EditOrderAdmin)               ║
║ Message Count: ${data.messages?.length || 0}
║ File Count: ${data.files?.length || 0}
║ Room: ${roomName}
╚════════════════════════════════════════════════════════════╝`);
      if (data.messages) {
        setMessages((prevMessages) => {
          const existingIds = new Set(prevMessages.map((msg) => msg.id));
          const incoming = data.messages.map((msg: any) => ({
            ...msg,
          }));
          const newMessages = incoming.filter(
            (msg: any) => !existingIds.has(msg.id),
          );
          const allMessages = [...prevMessages, ...newMessages];

          return allMessages.sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );
        });
      }
      if (data.files) {
        setUploadedFiles(data.files);
      }
    };

    const handleNewMessage = (data: any) => {
      console.log(`
╔════════════════════════════════════════════════════════════╗
║ � NEW MESSAGE RECEIVED (EditOrderAdmin)                   ║
║ Message ID: ${data.message?.id}
║ From: ${data.message?.sender}
║ Content: ${data.message?.content?.substring(0, 50)}...
║ Room: ${roomName}
╚════════════════════════════════════════════════════════════╝`);
      if (data.message) {
        const incoming = { ...data.message } as ChatMessage;
        setMessages((prev) => {
          if (prev.some((msg) => msg.id === incoming.id)) {
            console.log("⚠️ Message already exists:", incoming.id);
            return prev;
          }
          const matchIdx = prev.findIndex((msg) => {
            const matches =
              msg.id < 0 &&
              msg.sender === incoming.sender &&
              msg.content === incoming.content &&
              Math.abs(
                new Date(msg.timestamp).getTime() -
                  new Date(incoming.timestamp).getTime(),
              ) < 10000;
            if (matches) {
              console.log("✅ Found match - temp msg:", {
                id: msg.id,
                sender: msg.sender,
              });
            }
            return matches;
          });
          if (matchIdx !== -1) {
            console.log(
              "🔄 Replacing temp at index",
              matchIdx,
              "with server message",
            );
            const copy = [...prev];
            copy[matchIdx] = incoming;
            return copy.sort(
              (a, b) =>
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime(),
            );
          }
          console.log(
            "📌 No match, adding new message with sender:",
            incoming.sender,
          );
          return [...prev, incoming].sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );
        });
      }
    };

    const handleNewFile = (data: any) => {
      console.log("New file received:", data);
      if (data.file) {
        setUploadedFiles((prev) => {
          if (
            prev.some(
              (f) =>
                f.fileName === data.file.fileName &&
                f.fileUrl === data.file.fileUrl,
            )
          )
            return prev;
          return [...prev, data.file];
        });
      }
    };

    const handleMessageDeleted = (data: any) => {
      console.log("Message deleted:", data);
      setMessages((prev) => prev.filter((msg) => msg.id !== data.messageId));
    };

    const handleMessageEdited = (data: any) => {
      console.log("Message edited:", data);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.messageId
            ? { ...msg, content: data.newContent }
            : msg,
        ),
      );
    };

    // IMPORTANT: Register ALL listeners FIRST before joining room
    console.log(`📝 Registering socket listeners for room: ${roomName}`);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("messageHistory", handleMessageHistory);
    socket.on("newMessage", handleNewMessage);
    socket.on("newFile", handleNewFile);
    socket.on("messageDeleted", handleMessageDeleted);
    socket.on("messageEdited", handleMessageEdited);
    console.log(`✅ All listeners registered for room: ${roomName}`);

    // NOW check if socket is already connected and join room
    if (socket.connected) {
      console.log(`🟢 Socket already connected, joining room: ${roomName}`);
      setIsConnected(true);
      socket.emit("joinOrder", Number(orderId));
      console.log(`📤 Emitted joinOrder for orderId: ${orderId}`);
    } else {
      console.log(
        `⏳ Socket not yet connected (will join in handleConnect event)`,
      );
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("messageHistory", handleMessageHistory);
      socket.off("newMessage", handleNewMessage);
      socket.off("newFile", handleNewFile);
      socket.off("messageDeleted", handleMessageDeleted);
      socket.off("messageEdited", handleMessageEdited);
    };
  }, [orderId]);

  // Auto-scroll DOAR pentru chat container, nu pentru toată pagina
  useEffect(() => {
    const chatContainer = document.getElementById("chat-messages");
    if (chatContainer) {
      const isNearBottom =
        chatContainer.scrollTop + chatContainer.clientHeight >=
        chatContainer.scrollHeight - 100;

      // Scroll doar dacă user-ul e aproape de jos în chat
      if (isNearBottom || messages.length <= 1) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Funcție pentru trimiterea mesajelor

  // În handleSendMessage din EditOrderAdmin.tsx
  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!orderId) return;
    if (!messageInput.trim() && files.length === 0 && images.length === 0)
      return;

    setSending(true);

    try {
      // Dacă avem doar text, trimite prin Socket.IO
      if (messageInput.trim() && files.length === 0 && images.length === 0) {
        console.log(`
╔════════════════════════════════════════════════════════════╗
║ 📤 SENDING MESSAGE (Admin - Socket.IO)                     ║
║ Order ID: ${orderId}
║ Sender: admin
║ Content: ${messageInput.trim().substring(0, 50)}...
╚════════════════════════════════════════════════════════════╝`);

        const socket = getAdminSocket();
        if (!socket) {
          alert("Socket not connected");
          return;
        }
        // append temporary message so admin sees it immediately
        const tempId = -Date.now();
        const tempMsg = {
          id: tempId,
          orderId: Number(orderId),
          sender: "admin",
          messageType: "text",
          content: messageInput.trim(),
          timestamp: new Date().toISOString(),
        } as ChatMessage;
        console.log("📝 Created temp message (Admin):", {
          id: tempMsg.id,
          sender: tempMsg.sender,
          content: tempMsg.content.substring(0, 30),
        });
        setMessages((prev) => [...prev, tempMsg]);

        socket.emit("sendMessage", {
          orderId: Number(orderId),
          sender: "admin",
          content: messageInput.trim(),
        });
        console.log("✉️ Emitted sendMessage event with sender: Admin");
        setMessageInput("");
      } else {
        // Dacă avem fișiere, trimite prin HTTP
        const formData = new FormData();

        if (messageInput.trim()) {
          formData.append("content", messageInput.trim());
        }

        files.forEach((file) => {
          formData.append("files", file);
          console.log("Adding file to FormData:", file.name);
        });

        images.forEach((image) => {
          formData.append("images", image);
          console.log("Adding image to FormData:", image.name);
        });

        console.log("Sending message with files via HTTP from Admin");

        await axios.post(`${API_URL}/api/orders/${orderId}/message`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("✅ Admin message with files sent successfully");

        const socket = getAdminSocket();
        if (!socket) return;

        // Emite evenimente socket pentru fișiere încărcate
        // append temp file entries and emit fileUploaded for each file/image
        files.forEach((file) => {
          const fileUrl = `${API_URL}/uploads/${file.name}`;
          const tempFileItem: FileItem = {
            fileName: file.name,
            fileUrl,
            uploadedBy: "admin",
            timestamp: new Date().toISOString(),
            type: "file",
          };
          setUploadedFiles((prev) => [...prev, tempFileItem]);
          setMessages((prev) => [
            ...prev,
            {
              id: -Date.now() - Math.floor(Math.random() * 1000),
              orderId: Number(orderId),
              sender: "admin",
              messageType: "file",
              content: `File: ${file.name}`,
              fileName: file.name,
              fileUrl,
              timestamp: new Date().toISOString(),
            } as ChatMessage,
          ]);

          socket.emit("fileUploaded", {
            orderId: Number(orderId),
            fileName: file.name,
            fileUrl,
            uploadedBy: "admin",
            type: "file",
          });
        });

        images.forEach((image) => {
          const fileUrl = `${API_URL}/uploads/${image.name}`;
          const tempFileItem: FileItem = {
            fileName: image.name,
            fileUrl,
            uploadedBy: "admin",
            timestamp: new Date().toISOString(),
            type: "image",
          };
          setUploadedFiles((prev) => [...prev, tempFileItem]);
          setMessages((prev) => [
            ...prev,
            {
              id: -Date.now() - Math.floor(Math.random() * 1000),
              orderId: Number(orderId),
              sender: "admin",
              messageType: "image",
              content: `Image: ${image.name}`,
              fileName: image.name,
              fileUrl,
              timestamp: new Date().toISOString(),
            } as ChatMessage,
          ]);

          socket.emit("fileUploaded", {
            orderId: Number(orderId),
            fileName: image.name,
            fileUrl,
            uploadedBy: "admin",
            type: "image",
          });
        });

        // Curăță formele
        setFiles([]);
        setImages([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (imageInputRef.current) imageInputRef.current.value = "";
        setMessageInput("");
      }
    } catch (error) {
      console.error("❌ Error sending admin message:", error);
      alert("Error sending message");
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order) return;

    try {
      const updates: Partial<Order> = {
        status,
        trackingNo: trackingNo || null,
        shippedDate: shippedDate ? new Date(shippedDate).toISOString() : null,
        estimatedDate: estimatedDate
          ? new Date(estimatedDate).toISOString()
          : null,
        shippedBy: shippedBy || null,
        jobType: jobType || null,
      };

      const response = await axios.put(
        `${API_URL}/admin/order/${order.id}`,
        updates,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Actualizează state-ul local
      setOrder((prev) =>
        prev
          ? {
              ...prev,
              status,
              trackingNo: trackingNo || null,
              shippedDate: shippedDate
                ? new Date(shippedDate).toISOString()
                : null,
              estimatedDate: estimatedDate
                ? new Date(estimatedDate).toISOString()
                : null,
              shippedBy: shippedBy || null,
              jobType: jobType || null,
            }
          : null,
      );

      alert("Order updated successfully!");
    } catch (error: unknown) {
      console.error("Error updating order:", error);
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        alert(axiosError.response?.data?.message || "Error updating order");
      } else {
        alert("Error updating order");
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleString();
    } catch {
      return "N/A";
    }
  };

  // Funcție pentru a afișa data curentă de shipping (din form sau din order)
  const getCurrentShippedDate = () => {
    if (shippedDate) {
      return new Date(shippedDate).toLocaleString();
    }
    return formatDate(order?.shippedDate);
  };

  const renderMessage = (msg: ChatMessage) => {
    const isMyMessage = msg.sender?.toLowerCase() === "admin";
    const isEditing = editingMessageId === msg.id;

    return (
      <div
        key={msg.id}
        className={`flex ${isMyMessage ? "justify-end" : "justify-start"} mb-3`}
      >
        <div
          className={`max-w-[70%] px-4 py-2 rounded-lg relative group ${
            isMyMessage
              ? "bg-green-700 text-white"
              : "bg-green-200 dark:bg-green-600 text-black dark:text-white"
          }`}
        >
          <div className="text-xs opacity-70 mb-1">
            {isMyMessage ? "Admin" : "Client"} • {formatDate(msg.timestamp)}
          </div>

          {isEditing ? (
            // Modul de editare
            <div className="space-y-2">
              <textarea
                value={editMessageContent}
                onChange={(e) => setEditMessageContent(e.target.value)}
                className="w-full p-2 text-sm bg-white dark:bg-gray-800 text-black dark:text-white border rounded"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleEditMessage(msg.id)}
                  className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                >
                  Save
                </button>
                <button
                  onClick={cancelEditMessage}
                  className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // Modul normal de afișare
            <>
              {msg.messageType === "text" && (
                <div className="break-words">{msg.content}</div>
              )}

              {msg.messageType === "file" && msg.fileName && (
                <div>
                  <div className="break-words mb-1">{msg.content}</div>
                  <button
                    onClick={() => handleDownload(msg.fileName!)}
                    className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-100 underline cursor-pointer"
                  >
                    📎 {msg.fileName}
                  </button>
                </div>
              )}

              {msg.messageType === "image" && msg.fileName && (
                <div>
                  <div className="break-words mb-2">{msg.content}</div>
                  <img
                    src={msg.fileUrl || ""}
                    alt={msg.fileName || "Uploaded image"}
                    className="max-w-[300px] rounded cursor-pointer hover:opacity-80"
                    onClick={() => handleDownload(msg.fileName!)}
                    title={`Click to download: ${msg.fileName}`}
                  />
                </div>
              )}

              {/* Butoane pentru admin - apar doar la hover */}
              {isMyMessage && (
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex gap-1">
                    
                    <button
  onClick={() => startEditMessage(msg.id, msg.content)}
  className="w-6 h-6 bg-gray-500 hover:bg-gray-200 text-white rounded flex items-center justify-center transition-colors"
  title="Edit message"
>
  <Pencil size={14} />
</button>
                    <button
  onClick={() => handleDeleteMessage(msg.id)}
  className="w-6 h-6 bg-gray-500 hover:bg-gray-400 text-white rounded flex items-center justify-center transition-colors"
  title="Delete message"
>
  <Trash size={14} />
</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderFileTable = () => {
    const fileItems = uploadedFiles.filter((item) => item.type === "file");
    const imageItems = uploadedFiles.filter((item) => item.type === "image");

    return (
      <div className="mt-6 p-4 border rounded bg-white dark:bg-neutral-600 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
          Uploaded Files & Images
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Files Column */}
          <div>
            <h4 className="font-medium mb-3 text-gray-700 dark:text-gray-300 border-b pb-2">
              Files ({fileItems.length})
            </h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {fileItems.length > 0 ? (
                fileItems.map((file, index) => (
                  <div
                    key={`file-${index}`}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-600 rounded"
                  >
                    <div className="flex-1 min-w-0">
                      <a
                        href={`${API_URL}/api/download/${file.fileName}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium truncate block"
                      >
                        📎 {file.fileName}
                      </a>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {file.uploadedBy} • {formatDate(file.timestamp)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No files uploaded
                </p>
              )}
            </div>
          </div>

          {/* Images Column */}
          <div>
            <h4 className="font-medium mb-3 text-gray-700 dark:text-gray-300 border-b pb-2">
              Images ({imageItems.length})
            </h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {imageItems.length > 0 ? (
                imageItems.map((image, index) => (
                  <div
                    key={`image-${index}`}
                    className="p-2 bg-gray-50 dark:bg-gray-600 rounded"
                  >
                    <a href={`${API_URL}/api/download/${image.fileName}`}>
                      <img
                        src={image.fileUrl}
                        alt={image.fileName}
                        className="max-w-full h-auto rounded cursor-pointer hover:opacity-80 transition-opacity mb-2"
                        style={{ maxHeight: "150px" }}
                        title="Click to download"
                      />
                    </a>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {image.uploadedBy} • {formatDate(image.timestamp)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No images uploaded
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#025951]"></div>
            <span className="text-lg text-gray-700 dark:text-gray-300">
              Loading order...
            </span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Order Not Found
            </h1>
            <button
              onClick={() => navigate("/admin")}
              className="px-4 py-2 bg-green-700 dark:bg-[#025951] text-white rounded-lg hover:bg-emerald-600 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Edit Order #{order.orderNo}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Reference: {order.orderRef}
              </p>
            </div>
          </div>

          {/* TOP ROW: Order Information + Update Order */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Order Information */}
            <div className="bg-neutral-50 dark:bg-neutral-600 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500 p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Order Information
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Order Number:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {order.orderNo}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Reference:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {order.orderRef}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Status:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          status === "Created"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : status === "Received"
                              ? "bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200"
                              : status === "In process"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                : status === "In pending"
                                  ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                  : status === "Dispatched"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                        }`}
                      >
                        {status || order.status}
                      </span>
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Job Type:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {jobType || order.jobType || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Order Date:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {formatDate(order.orderDate)}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Estimated Date:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {estimatedDate
                        ? new Date(estimatedDate).toLocaleDateString()
                        : order.estimatedDate
                          ? formatDate(order.estimatedDate)
                          : "N/A"}
                      <br />
                      <span className="text-xs text-gray-500">
                        (Business days only)
                      </span>
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Shipped By:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {order.shippedBy || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Shipped Date:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {getCurrentShippedDate()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Tracking No:
                    </span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {trackingNo || order.trackingNo || "N/A"}
                    </p>
                  </div>
                </div>

                {order.message && (
                  <div className="pt-4 border-t border-gray-200 dark:border-neutral-600">
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Client Message:
                    </span>
                    <p className="text-gray-900 dark:text-white mt-1 bg-gray-50 dark:bg-neutral-600 p-3 rounded-lg">
                      {order.message}
                    </p>
                  </div>
                )}

                {order.description && (
                  <div className="pt-4 border-t border-gray-200 dark:border-neutral-600">
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Description:
                    </span>
                    <p className="text-gray-900 dark:text-white mt-1 bg-gray-50 dark:bg-neutral-600 p-3 rounded-lg">
                      {order.description}
                    </p>
                  </div>
                )}

                {order.imageUrl && (
                  <div className="pt-4 border-t border-gray-200 dark:border-neutral-600">
                    <span className="font-medium text-gray-600 dark:text-gray-400">
                      Attached Image:
                    </span>
                    <img
                      src={order.imageUrl}
                      alt="Order attachment"
                      className="mt-2 max-w-full h-auto rounded-lg border"
                      style={{ maxHeight: "200px" }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Update Order Form */}
            <div className="bg-neutral-50 dark:bg-neutral-600 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500 p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Update Order
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent"
                    required
                  >
                    <option value="Created">Created</option>
                    <option value="Received">Received</option>
                    <option value="In process">In process</option>
                    <option value="In pending">In pending</option>
                    <option value="Dispatched">Dispatched</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Job Type
                  </label>
                  <select
                    value={jobType}
                    onChange={(e) => setJobType(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent"
                  >
                    <option value="">Select job type...</option>
                    {JOB_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Estimated Date{" "}
                    <span className="text-xs text-gray-500">
                      (Working days only - excluding weekends)
                    </span>
                  </label>
                  <input
                    type="date"
                    value={estimatedDate}
                    onChange={(e) => setEstimatedDate(e.target.value)}
                    min={getMinDate()}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Shipped By
                  </label>
                  <select
                    value={shippedBy}
                    onChange={(e) => setShippedBy(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent"
                  >
                    <option value="">Select shipping method...</option>
                    {SHIPPING_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Tracking Number
                  </label>
                  <input
                    type="text"
                    value={trackingNo}
                    onChange={(e) => setTrackingNo(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent"
                    placeholder="Enter tracking number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Shipped Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={shippedDate}
                    onChange={(e) => setShippedDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-green dark:bg-gradient-to-r from-[#025951] to-emerald-500 text-white py-3 rounded-lg font-medium hover:from-emerald-600 hover:to-emerald-400 transition-all duration-200 shadow-lg"
                >
                  Update Order
                </button>
              </form>
            </div>
          </div>

          {/* BOTTOM ROW: Chat pe toată lățimea */}
          <div className="bg-white dark:bg-neutral-700 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-600 overflow-hidden">
            {/* Chat Header */}
            <div className="bg-green dark:bg-gradient-to-r from-[#025951] to-emerald-500 text-white p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Order Chat</h2>
                <div
                  className={`flex items-center space-x-2 ${isConnected ? "text-green-100" : "text-red-100"}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${isConnected ? "bg-green-300" : "bg-red-300"}`}
                  ></div>
                  <span className="text-sm">
                    {isConnected ? "Connected" : "Disconnected"}
                  </span>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div
              id="chat-messages"
              className="h-96 overflow-y-auto p-4 bg-gray-50 dark:bg-neutral-600"
            >
              {" "}
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg) => renderMessage(msg))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200 dark:border-neutral-500 bg-white dark:bg-neutral-700">
              <form onSubmit={handleSendMessage} className="space-y-3">
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                  className="w-full p-3 border border-gray-300 dark:border-neutral-500 rounded-lg bg-white dark:bg-neutral-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#025951] focus:border-transparent resize-none"
                  rows={3}
                />

                {/* File Upload Section */}
                <div className="flex flex-wrap gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => setFiles(Array.from(e.target.files || []))}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.zip,.rar"
                  />
                  <input
                    ref={imageInputRef}
                    type="file"
                    multiple
                    onChange={(e) =>
                      setImages(Array.from(e.target.files || []))
                    }
                    className="hidden"
                    accept="image/*"
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors flex items-center gap-2"
                  >
                    <Paperclip size={16} />
                    Files
                  </button>

                  <button
  type="button"
  onClick={() => imageInputRef.current?.click()}
  className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors flex items-center gap-2"
>
  <ImageDown size={16} />
  Images
</button>
                </div>

                {/* Selected Files Display */}
                {(files.length > 0 || images.length > 0) && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>
                      Selected: {files.length} files, {images.length} images
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {files.map((file, index) => (
                        <span
                          key={`file-${index}`}
                          className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded text-xs"
                        >
                          📎 {file.name}
                        </span>
                      ))}
                      {images.map((image, index) => (
                        <span
                          key={`image-${index}`}
                          className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded text-xs"
                        >
                          🖼️ {image.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={
                    sending ||
                    (!messageInput.trim() &&
                      files.length === 0 &&
                      images.length === 0)
                  }
                  className="w-full bg-green dark:bg-gradient-to-r from-[#025951] to-emerald-500 text-white py-2 rounded-lg font-medium hover:from-emerald-600 hover:to-emerald-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>

          {/* File Display Table */}
          {renderFileTable()}

          <div className="fixed bottom-6 right-6">
            <button
              onClick={() => navigate("/admin")}
              className="px-6 py-3 bg-green-700 dark:bg-gradient-to-r from-[#025951] to-emerald-500 text-white rounded-full shadow-lg hover:from-emerald-600 hover:to-emerald-400 transition-all duration-200 flex items-center gap-2"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EditOrderAdmin;
